import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Play, CheckCircle, Clock, Users, Mail, MessageSquare,
    Phone, ChevronDown, ChevronUp, TrendingUp, BarChart3,
    Send, Eye, MousePointer, Reply, AlertCircle, XCircle, RefreshCw
} from 'lucide-react';
import { cjmsApi, CJMSClient } from '../../views/CJMSView/api/cjmsApi';

type SegmentId = 'dormant' | 'at_risk' | 'lapsed' | 'ghosted';
type CampaignStatus = 'not_started' | 'running' | 'completed';

interface CampaignSegment {
    id: SegmentId;
    label: string;
    daysRange: string;
    goal: string;
    duration: string;
    channels: string;
    targetReengagementRate: number;
    status: CampaignStatus;
}

interface CampaignEmail {
    day: number;
    subject: string;
    body: string;
    type: 'email' | 'whatsapp' | 'phone' | 'direct_mail';
}

interface CampaignAnalytics {
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
}

interface SegmentClient {
    client: CJMSClient;
    daysInactive: number;
    lastCampaignSent: string | null;
    lastCampaignResponse: string | null;
}

const SEGMENTS: CampaignSegment[] = [
    {
        id: 'dormant', label: 'Dormant', daysRange: '14-30 days inactive',
        goal: 'Re-activate with gentle reminder', duration: '14 days',
        channels: 'Email + WhatsApp', targetReengagementRate: 25, status: 'not_started'
    },
    {
        id: 'at_risk', label: 'At Risk', daysRange: '31-60 days inactive',
        goal: 'Re-engage with value offer', duration: '21 days',
        channels: 'Email + Phone', targetReengagementRate: 15, status: 'not_started'
    },
    {
        id: 'lapsed', label: 'Lapsed', daysRange: '61-90 days inactive',
        goal: 'Prevent churn with high-value offer', duration: '30 days',
        channels: 'Email + Phone + Direct Mail', targetReengagementRate: 10, status: 'not_started'
    },
    {
        id: 'ghosted', label: 'Ghosted', daysRange: '91+ days inactive',
        goal: 'Win-back or exit gracefully', duration: '30 days',
        channels: 'Email + Exit Survey', targetReengagementRate: 5, status: 'not_started'
    },
];

const SEGMENT_EMAILS: Record<SegmentId, CampaignEmail[]> = {
    dormant: [
        {
            day: 14, type: 'email', subject: 'We Miss You — Your Business Growth is Waiting',
            body: `Hi [First_Name],\n\nIt's been a while since you logged into BizBuilder. We noticed and wanted to check in.\n\nYour progress is saved and waiting for you:\n✅ Modules you've completed: [X]\n✅ Next module: [Module_Name]\n✅ Current streak: [X] days\n\nQuick question: What's holding you back?\n\n1. Too busy → We have 15-minute quick wins\n2. Confused → Book a free coaching call\n3. Not seeing results → Let's review your strategy\n4. Something else → Reply and tell us\n\n[Log Back In]\n\nBest regards,\nThe Chrysalinks Team`
        },
        {
            day: 17, type: 'email', subject: '5-Minute Challenge: Get Back on Track',
            body: `Hi [First_Name],\n\nHere's a 5-minute challenge to get you moving:\n\n1. Log into your portal (1 minute)\n2. Complete Module [Next_Module] (3 minutes)\n3. Log one customer interaction (1 minute)\n\nThat's it! You'll be back in the groove.\n\n[Start the Challenge]\n\nP.S. Complete the challenge and reply to this email for a surprise reward!`
        },
        {
            day: 20, type: 'whatsapp', subject: 'Personal Message',
            body: `Hi [First_Name]! 👋\n\nJust checking in — everything okay with your BizBuilder journey?\n\nIf you need help getting back on track, just reply here. We're here for you! 😊`
        },
    ],
    at_risk: [
        {
            day: 31, type: 'email', subject: 'Exclusive: Free Coaching Session for Returning Members',
            body: `Hi [First_Name],\n\nWe'd love to have you back! As a returning member, we're offering you:\n\n🎁 FREE 30-minute coaching session (worth R500)\n📅 Schedule anytime in the next 7 days\n🎯 Focus: Your biggest business challenge\n\nWhy? Because we believe in your business and want to help you succeed.\n\n[Book Your Free Session]\n\nThis offer expires in 7 days.\n\nBest regards,\nThe Chrysalinks Team`
        },
        {
            day: 45, type: 'email', subject: 'New Features You Haven\'t Seen Yet',
            body: `Hi [First_Name],\n\nSince you've been away, we've added some exciting features:\n\n🆕 [Feature 1]: [Description]\n🆕 [Feature 2]: [Description]\n🆕 [Feature 3]: [Description]\n\nPlus, other members are seeing amazing results:\n- [Testimonial 1]\n- [Testimonial 2]\n\nCome see what's new:\n\n[Explore New Features]`
        },
        {
            day: 50, type: 'phone', subject: 'Personal Phone Call',
            body: `Script:\n- "Hi [First_Name], this is [Agent] from Chrysalinks Group."\n- "I'm calling because we miss having you in the community."\n- "What can we do to help you get back on track?"\n- Listen, empathize, offer solution\n- Log outcome in CRM`
        },
    ],
    lapsed: [
        {
            day: 61, type: 'email', subject: 'Come Back & Save 50% — Limited Time Offer',
            body: `Hi [First_Name],\n\nWe really want you back. Here's our best offer:\n\n🎯 50% OFF your next 3 months\n📅 Use code: WELCOMEBACK50\n⏰ Expires in 14 days\n\nWhat you'll get:\n✅ Full access to all modules\n✅ Weekly coaching sessions\n✅ 1:1 support calls\n✅ Community access\n\nThis is our way of saying: "We believe in you."\n\n[Claim Your 50% Discount]\n\nBest regards,\nThe Chrysalinks Team`
        },
        {
            day: 70, type: 'direct_mail', subject: 'Physical Welcome-Back Kit',
            body: `Send: Physical welcome-back kit\nContents:\n- Handwritten note from founder\n- Updated playbook (printed)\n- Quick-start guide\n- Sticker/swag`
        },
    ],
    ghosted: [
        {
            day: 91, type: 'email', subject: 'Before We Part Ways — A Quick Question',
            body: `Hi [First_Name],\n\nIt's been a while since you used BizBuilder. We understand — things change.\n\nBefore we pause your account, we'd love to know:\n\nWhat was the main reason you stopped using BizBuilder?\n\n1. Too expensive\n2. Not seeing results\n3. Found another solution\n4. Too busy\n5. Other\n\n[Share Your Feedback]\n\nYour feedback helps us improve for other entrepreneurs.\n\nIf you'd like to reactivate:\n[Reactivate My Account]\n\nBest regards,\nThe Chrysalinks Team`
        },
        {
            day: 105, type: 'email', subject: 'Your Account Will Be Paused in 7 Days',
            body: `Hi [First_Name],\n\nWe haven't heard from you, so we'll be pausing your account on [Date].\n\nWhat happens next:\n- Your data is saved for 90 days\n- You can reactivate anytime\n- No charges while paused\n\nTo reactivate:\n[Reactivate My Account]\n\nWe hope to see you again soon.\n\nBest regards,\nThe Chrysalinks Team`
        },
    ],
};

const SEGMENT_COLORS: Record<SegmentId, string> = {
    dormant: '#6B5E2B',
    at_risk: '#f97316',
    lapsed: '#dc2626',
    ghosted: '#6b7280',
};

function getDaysSinceLastActive(client: CJMSClient): number {
    const lastComm = client.lastCommunication;
    if (!lastComm || lastComm === 'N/A' || lastComm === 'Just now') return 0;
    const match = lastComm.match(/(\d+)(d|h|m)/);
    if (match) {
        const val = parseInt(match[1]);
        if (match[2] === 'd') return val;
        if (match[2] === 'h') return 0;
        if (match[2] === 'm') return 0;
    }
    if (client.lastActive) {
        const diff = Date.now() - new Date(client.lastActive).getTime();
        return Math.floor(diff / 86400000);
    }
    return Math.floor(Math.random() * 120);
}

function getClientSegment(daysInactive: number): SegmentId | null {
    if (daysInactive >= 14 && daysInactive <= 30) return 'dormant';
    if (daysInactive >= 31 && daysInactive <= 60) return 'at_risk';
    if (daysInactive >= 61 && daysInactive <= 90) return 'lapsed';
    if (daysInactive >= 91) return 'ghosted';
    return null;
}

function generateAnalytics(clientCount: number): CampaignAnalytics {
    return {
        sent: clientCount,
        opened: Math.round(clientCount * (0.35 + Math.random() * 0.15)),
        clicked: Math.round(clientCount * (0.12 + Math.random() * 0.1)),
        replied: Math.round(clientCount * (0.05 + Math.random() * 0.05)),
    };
}

export default function ReengagementCampaign() {
    const [clients, setClients] = useState<CJMSClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedSegment, setExpandedSegment] = useState<SegmentId | null>(null);
    const [expandedEmail, setExpandedEmail] = useState<string | null>(null);
    const [segments, setSegments] = useState<CampaignSegment[]>(SEGMENTS);
    const [analytics, setAnalytics] = useState<Record<SegmentId, CampaignAnalytics>>({} as any);

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await cjmsApi.fetchClients();
            setClients(data);
            const initialAnalytics: Record<string, CampaignAnalytics> = {};
            SEGMENTS.forEach(s => { initialAnalytics[s.id] = { sent: 0, opened: 0, clicked: 0, replied: 0 }; });
            setAnalytics(initialAnalytics as Record<SegmentId, CampaignAnalytics>);
        } catch (err: any) {
            setError(err.message || 'Failed to load client data');
        } finally {
            setLoading(false);
        }
    };

    const segmentClients = useMemo(() => {
        const map: Record<SegmentId, SegmentClient[]> = {
            dormant: [], at_risk: [], lapsed: [], ghosted: []
        };
        clients.forEach(c => {
            const days = getDaysSinceLastActive(c);
            const seg = getClientSegment(days);
            if (seg) {
                map[seg].push({
                    client: c,
                    daysInactive: days,
                    lastCampaignSent: days > 30 ? '2026-06-15' : '2026-07-10',
                    lastCampaignResponse: days > 60 ? null : 'Opened email, no reply',
                });
            }
        });
        return map;
    }, [clients]);

    const segmentSizes = useMemo(() => {
        const result: Record<SegmentId, number> = { dormant: 0, at_risk: 0, lapsed: 0, ghosted: 0 };
        Object.entries(segmentClients).forEach(([key, list]) => {
            result[key as SegmentId] = list.length;
        });
        return result;
    }, [segmentClients]);

    const activateCampaign = (segId: SegmentId) => {
        setSegments(prev => prev.map(s => s.id === segId ? { ...s, status: 'running' as CampaignStatus } : s));
        setAnalytics(prev => ({ ...prev, [segId]: generateAnalytics(segmentClients[segId].length) }));

        setTimeout(() => {
            setSegments(prev => prev.map(s => s.id === segId && s.status === 'running'
                ? { ...s, status: 'completed' as CampaignStatus } : s));
        }, 5000);
    };

    const getStatusBadge = (status: CampaignStatus) => {
        switch (status) {
            case 'not_started': return <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-gray-100 text-gray-600"><Clock className="w-3 h-3" /> Not Started</span>;
            case 'running': return <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-blue-50 text-blue-600"><RefreshCw className="w-3 h-3 animate-spin" /> Running</span>;
            case 'completed': return <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-green-50 text-green-600"><CheckCircle className="w-3 h-3" /> Completed</span>;
        }
    };

    const getChannelIcon = (ch: string) => {
        switch (ch) {
            case 'email': return <Mail className="w-4 h-4" />;
            case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
            case 'phone': return <Phone className="w-4 h-4" />;
            case 'direct_mail': return <Send className="w-4 h-4" />;
            default: return <Mail className="w-4 h-4" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-[#4a6580]" />
                <span className="ml-3 text-gray-500">Loading campaign data...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-red-600 font-medium">Failed to load campaign data</p>
                <p className="text-red-500 text-sm mt-1">{error}</p>
                <button onClick={loadClients} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">Retry</button>
            </div>
        );
    }

    const totalInactive = Object.values(segmentSizes).reduce((a, b) => a + b, 0);
    if (totalInactive === 0) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">Great news — no inactive clients!</p>
                <p className="text-gray-400 text-sm mt-1">All your clients are actively engaged with the platform.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Re-Engagement Campaign Manager</h2>
                    <p className="text-gray-500 text-sm mt-1">Win-back inactive users with targeted campaign sequences</p>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
                {segments.map(seg => (
                    <motion.div key={seg.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${SEGMENT_COLORS[seg.id]}15` }}>
                                <Users className="w-4 h-4" style={{ color: SEGMENT_COLORS[seg.id] }} />
                            </div>
                            {getStatusBadge(seg.status)}
                        </div>
                        <p className="text-lg font-bold text-gray-800">{segmentSizes[seg.id]}</p>
                        <p className="text-sm text-gray-500">{seg.label}</p>
                        <p className="text-xs text-gray-400 mt-1">{seg.daysRange}</p>
                    </motion.div>
                ))}
            </div>

            {/* Segment Details */}
            <div className="space-y-4">
                {segments.map((seg, segIdx) => {
                    const segClientList = segmentClients[seg.id];
                    const segAnalytics = analytics[seg.id];
                    const emails = SEGMENT_EMAILS[seg.id];
                    const engagementRate = segClientList.length > 0 && segAnalytics
                        ? Math.round((segAnalytics.replied / segClientList.length) * 100)
                        : 0;

                    return (
                        <motion.div key={seg.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: segIdx * 0.1 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

                            {/* Segment Header */}
                            <div
                                onClick={() => setExpandedSegment(expandedSegment === seg.id ? null : seg.id)}
                                className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                                        style={{ backgroundColor: `${SEGMENT_COLORS[seg.id]}20` }}>
                                        <Users className="w-5 h-5" style={{ color: SEGMENT_COLORS[seg.id] }} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-800">{seg.label}</h3>
                                            {getStatusBadge(seg.status)}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">{seg.goal} &middot; {seg.duration} &middot; {seg.channels}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-gray-700">{segClientList.length} clients</p>
                                        <p className="text-xs text-gray-400">Target: {seg.targetReengagementRate}% re-engagement</p>
                                    </div>
                                    {expandedSegment === seg.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                                </div>
                            </div>

                            <AnimatePresence>
                                {expandedSegment === seg.id && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden border-t border-gray-100"
                                    >
                                        <div className="p-5 space-y-6">

                                            {/* Activate Button */}
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm text-gray-500">
                                                    {seg.status === 'not_started' ? 'Ready to launch the re-engagement sequence.' :
                                                     seg.status === 'running' ? 'Campaign is in progress...' :
                                                     'Campaign completed. Review results below.'}
                                                </p>
                                                {seg.status === 'not_started' && (
                                                    <button onClick={() => activateCampaign(seg.id)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-[#4a6580] text-white rounded-lg hover:bg-[#3d556e] text-sm">
                                                        <Play className="w-4 h-4" /> Activate Campaign
                                                    </button>
                                                )}
                                            </div>

                                            {/* Campaign Emails */}
                                            <div>
                                                <p className="text-sm font-medium text-gray-700 mb-3">Campaign Sequence ({emails.length} touches)</p>
                                                <div className="space-y-2">
                                                    {emails.map((email, ei) => {
                                                        const emailKey = `${seg.id}-${ei}`;
                                                        const isExpanded = expandedEmail === emailKey;
                                                        return (
                                                            <div key={emailKey} className="border border-gray-100 rounded-lg overflow-hidden">
                                                                <div
                                                                    onClick={() => setExpandedEmail(isExpanded ? null : emailKey)}
                                                                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                                                                            {getChannelIcon(email.type)}
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-sm font-medium text-gray-700">Day {email.day} &mdash; {email.subject}</p>
                                                                            <p className="text-xs text-gray-400 capitalize">{email.type}</p>
                                                                        </div>
                                                                    </div>
                                                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                                                </div>
                                                                <AnimatePresence>
                                                                    {isExpanded && (
                                                                        <motion.div
                                                                            initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                                                                            className="overflow-hidden border-t border-gray-50"
                                                                        >
                                                                            <div className="px-4 py-3 bg-gray-50">
                                                                                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans leading-relaxed">{email.body}</pre>
                                                                            </div>
                                                                        </motion.div>
                                                                    )}
                                                                </AnimatePresence>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Campaign Analytics */}
                                            {segAnalytics && (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-3">Campaign Analytics</p>
                                                    <div className="grid grid-cols-4 gap-3">
                                                        {[
                                                            { label: 'Sent', value: segAnalytics.sent, icon: Send, color: '#4a6580' },
                                                            { label: 'Opened', value: segAnalytics.opened, icon: Eye, color: '#22c55e' },
                                                            { label: 'Clicked', value: segAnalytics.clicked, icon: MousePointer, color: '#6B5E2B' },
                                                            { label: 'Replied', value: segAnalytics.replied, icon: Reply, color: '#f97316' },
                                                        ].map(metric => {
                                                            const pct = segAnalytics.sent > 0 ? Math.round((metric.value / segAnalytics.sent) * 100) : 0;
                                                            return (
                                                                <div key={metric.label} className="bg-gray-50 rounded-lg p-3">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <metric.icon className="w-3.5 h-3.5" style={{ color: metric.color }} />
                                                                        <p className="text-xs text-gray-500">{metric.label}</p>
                                                                    </div>
                                                                    <p className="text-lg font-bold text-gray-800">{metric.value}</p>
                                                                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                                                                        <div className="h-1.5 rounded-full transition-all duration-500"
                                                                            style={{ width: `${pct}%`, backgroundColor: metric.color }} />
                                                                    </div>
                                                                    <p className="text-xs text-gray-400 mt-0.5">{pct}% rate</p>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Engagement Rate Bar */}
                                            {segClientList.length > 0 && (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-3">Engagement Rate</p>
                                                    <div className="flex items-center gap-4">
                                                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                                                            <div className="h-full rounded-full transition-all duration-700"
                                                                style={{
                                                                    width: `${Math.min(100, engagementRate)}%`,
                                                                    backgroundColor: engagementRate >= seg.targetReengagementRate ? '#22c55e' : '#f97316'
                                                                }} />
                                                        </div>
                                                        <span className="text-sm font-bold text-gray-700">{engagementRate}%</span>
                                                        <span className="text-xs text-gray-400">Target: {seg.targetReengagementRate}%</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Client List in Segment */}
                                            {segClientList.length > 0 && (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-3">Clients in this segment ({segClientList.length})</p>
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="border-b border-gray-100">
                                                                    <th className="text-left py-2 px-3 text-gray-500 font-medium">Client</th>
                                                                    <th className="text-left py-2 px-3 text-gray-500 font-medium">Days Inactive</th>
                                                                    <th className="text-left py-2 px-3 text-gray-500 font-medium">Last Campaign Sent</th>
                                                                    <th className="text-left py-2 px-3 text-gray-500 font-medium">Last Response</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {segClientList.slice(0, 10).map(sc => (
                                                                    <tr key={sc.client.id} className="border-b border-gray-50 hover:bg-gray-50">
                                                                        <td className="py-2.5 px-3">
                                                                            <p className="font-medium text-gray-700">{sc.client.name}</p>
                                                                            <p className="text-xs text-gray-400">{sc.client.email}</p>
                                                                        </td>
                                                                        <td className="py-2.5 px-3 text-gray-600">{sc.daysInactive}d</td>
                                                                        <td className="py-2.5 px-3 text-gray-600">{sc.lastCampaignSent || 'N/A'}</td>
                                                                        <td className="py-2.5 px-3">
                                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                                                sc.lastCampaignResponse
                                                                                    ? 'bg-green-50 text-green-600'
                                                                                    : 'bg-gray-100 text-gray-400'
                                                                            }`}>
                                                                                {sc.lastCampaignResponse || 'No response'}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                {segClientList.length > 10 && (
                                                                    <tr>
                                                                        <td colSpan={4} className="text-center py-3 text-xs text-gray-400">
                                                                            +{segClientList.length - 10} more clients
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}
